<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<!-- mouse handling differs from key handling. both will be used -->
<template>
  <div
    class="mmm-nav-sub-menu-cat-item"
    :class="[
      `mmm-nav-sub-menu-cat-item--depth-${depth}`,
      { 'mmm-nav-sub-menu-cat-item--active': isActive || item.active },
    ]"
  >
    <a
      :href="item.path"
      ref="link"
      @mousedown="handleMouseDown"
      role="menuitem"
      tabindex="-1"
      :aria-haspopup="item.children.length > 0 ? 'true' : undefined"
      :aria-expanded="isSubNavOpen ? 'true' : 'false'"
      :aria-controls="item.children.length > 0 ? `submenu-${path.join('-')}` : undefined"
      @mouseenter="updateSubMenu(index)"
      @touchstart="handleTouch($event, index)"
      class="mmm-nav-sub-menu-cat-item__link"
    >
      {{ sanitize(item.title) }}
      <span v-if="item.children.length > 0" class="mmm-nav-sub-menu-cat-item__icon">
        <IconChevron />
      </span>
    </a>
    <div
      v-if="isSubNavOpen"
      class="mmm-nav-sub-menu-cat-item__sub"
      :class="['mmm-nav-sub-menu-cat-item__sub--' + depth]"
      role="menu"
      :id="`submenu-${path.join('-')}`"
      :aria-label="`Submenü von ${item.title}`"
      :style="{
        transform: depth === 0 ? `translateX(calc(${(100 / 3) * parentIndex}% - 16px))` : '',
      }"
    >
      <NavItemDesktop
        :path="[...props.path, i]"
        @update-selection="handleUpdateSelection($event)"
        :subMenuSelection="subMenuSelection"
        :is-keyboard-active="isKeyboardActive"
        v-for="(child, i) in item.children"
        :parent-index="index"
        :item="child"
        :key="child.path"
        :depth="depth + 1"
        :index="i"
        :ref="
          el => {
            if (el && typeof el === 'object' && 'handleKeyLeft' in el) {
              items[i] = el as unknown as INavItemDesktopComponent;
            } else {
              items[i] = null;
            }
          }
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { sanitize, type ISubHeaderNavItem } from '../helper';

import NavItemDesktop from './NavItemDesktop.ce.vue';

import IconChevron from '~icons/mmm/chevron-right.svg';

const items = ref<(INavItemDesktopComponent | null)[]>([]);
const link = ref();

let currentIndex = 0;

const props = defineProps<{
  item: ISubHeaderNavItem;
  depth: number;
  index: number;
  subMenuSelection: number[];
  parentIndex: number;
  path: number[];
  isKeyboardActive: boolean;
}>();

const isSubNavOpen = computed(() => {
  if (props.item.children.length === 0) return false;
  return isActive.value;
});

const isActive = computed(() => {
  return props.path.every((val, i) => props.subMenuSelection[i] === val);
});

let hasTouched = false;
let touchTarget: HTMLElement | null = null;

function handleTouch(event: TouchEvent, index: number) {
  if (props.item.children.length > 0) {
    if (hasTouched && touchTarget !== event.target) {
      event.preventDefault();
      hasTouched = false;
      touchTarget = null;
      updateByTouch(index);
    } else if (!hasTouched) {
      event.preventDefault();
      updateByTouch(index);
      hasTouched = true;
      touchTarget = event.target as HTMLElement;
    }
  }
}

function updateByTouch(index: number) {
  const tmp = [...props.path];
  if (tmp.length === 2 + props.depth) {
    tmp.push(index);
  } else {
    tmp.splice(2 + props.depth, Infinity);
    tmp[2 + props.depth] = index;
  }
  emit('updateSelection', tmp);
}

function handleMouseDown(event: MouseEvent) {
  event.stopPropagation();
}

function handleKeyLeft() {
  if (props.subMenuSelection.length === 4 + props.depth) {
    const tmp = [...props.subMenuSelection];
    tmp.pop();
    emit('updateSelection', tmp);
    focus();
    currentIndex = 0;
  } else {
    const index = props.subMenuSelection[3 + props.depth];
    const item = items.value[index];
    item?.handleKeyLeft();
  }
}

function handleKeyRight() {
  if (props.item.children.length > 0) {
    if (props.subMenuSelection.length === 3 + props.depth) {
      if (props.item.children) {
        const tmp = [...props.subMenuSelection, 0];
        items.value[0]?.focus();
        emit('updateSelection', tmp);
      }
    } else {
      const item = items.value[props.subMenuSelection[3 + props.depth]];
      if (item) {
        item.handleKeyRight();
      }
    }
  }
}

function handleKeyDown() {
  if (props.path.length === props.subMenuSelection.length - 1) {
    currentIndex++;
    if (currentIndex > items.value.length - 1) {
      currentIndex = 0;
    }
    focusElementByIndex(currentIndex);
  } else {
    items.value[currentIndex]?.handleKeyDown();
  }
}

function handleKeyUp() {
  if (props.path.length === props.subMenuSelection.length - 1) {
    currentIndex--;
    if (currentIndex < 0) {
      currentIndex = items.value.length - 1;
    }
    focusElementByIndex(currentIndex);
  } else {
    items.value[currentIndex]?.handleKeyDown();
  }
}

function handleEscape() {
  if (props.subMenuSelection.length === 4 + props.depth) {
    handleKeyLeft();
  } else {
    const index = props.subMenuSelection[3 + props.depth];
    const item = items.value[index];
    item?.handleEscape();
  }
}

function focus() {
  link.value?.focus();
}

function focusElementByIndex(index: number) {
  const item = items.value[index];
  if (item) {
    item.focus();
  }

  const temp = [...props.subMenuSelection];
  temp[temp.length - 1] = index;
  emit('updateSelection', temp);
}

function updateSubMenu(index: number) {
  if (props.isKeyboardActive) return;
  const tmp = [...props.subMenuSelection];
  if (tmp.length === 2 + props.depth) {
    tmp.push(index);
  } else {
    tmp.splice(2 + props.depth, Infinity);
    tmp[2 + props.depth] = index;
  }
  emit('updateSelection', tmp);
}

function handleUpdateSelection(selection: number[]) {
  emit('updateSelection', selection);
}

const emit = defineEmits(['updateSelection', 'updateSelectionWithoutFocus']);

defineExpose({
  handleKeyLeft,
  handleKeyRight,
  handleKeyUp,
  handleKeyDown,
  focus,
  handleEscape,
  item: props.item,
});

export interface INavItemDesktopComponent {
  handleKeyLeft(): void;
  handleKeyRight(): void;
  handleKeyUp(): void;
  handleKeyDown(): void;
  focus(): void;
  handleEscape(): void;
  item: ISubHeaderNavItem;
}
</script>
<style scoped lang="scss">
.mmm-nav-sub-menu-cat-item {
  display: inline-flex;
  gap: 25px;
  margin: 0;
  padding: 0 12px;
  list-style-type: none;
  align-items: center;
  width: calc(100% - 32px);
  border-bottom: 1px solid var(--sub-menu-item-hover-background);

  &--depth-0 {
    border-bottom: 1px solid var(--sub-menu-item-active-background);

    &:has(+ .mmm-nav-sub-menu-cat-item:hover),
    &:has(+ .mmm-nav-sub-menu-cat-item:focus),
    &:has(+ .mmm-nav-sub-menu-cat-item--active) {
      border-color: transparent;
    }

    &:hover,
    &:focus {
      border-color: transparent;
    }
  }

  &--active {
    > .mmm-nav-sub-menu-cat-item__link {
      margin: 0 -12px;
      padding: 8px 12px;
      border-radius: 4px;
      color: var(--current-accent);
      background-color: var(--sub-menu-item-hover-background);
    }

    &.mmm-nav-sub-menu-cat-item--depth-0 {
      > .mmm-nav-sub-menu-cat-item__link {
        background-color: var(--sub-menu-item-active-background);
      }
    }
  }

  &__link {
    display: inline-flex;
    padding: 8px 0;
    font-size: 16px;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    font-weight: 700;
    text-decoration: none;
    color: var(--color-foreground-text);

    &:hover,
    &:focus {
      color: var(--current-accent);
      margin: 0 -12px;
      padding: 8px 12px;
      border-radius: 4px;
      background-color: var(--sub-menu-item-hover-background);
    }
  }

  &__icon {
    svg {
      height: 12px;
      width: autO;
    }
  }

  &__sub {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    padding: 74px 12px 12px;
    padding-top: 74px;
    left: calc(100% / 3);
    background-color: var(--overlay-background-third-level);
    box-sizing: border-box;

    &--0 {
      left: calc(100% / 3);

      .mmm-nav-sub-menu-cat-item {
        width: calc(33% - 24px);
        display: block;

        &__link {
        }
      }
    }
  }
}

.mmm-nav-sub-menu-cat-item--active {
  .mmm-nav-sub-menu-cat-item-depth--0 {
  }
}

.lower-level {
  display: none;
  position: absolute;
  list-style-type: none;
  height: calc(var(--default-min-height) + 10px);
  left: 100%;
  list-style-type: none;
  margin: -60px 0 -6px 0;
  padding: 58px var(--sub-level-gap) 30px;
  top: 0;
  width: 100%;
  z-index: 10;

  &--visible,
  &:hover {
    display: block;
  }

  &::before,
  &:after {
    content: '';
    position: absolute;
    background-color: var(--overlay-background-second-level);
    width: 100%;
    display: block;
    top: 0;
    left: var(--sub-level-gap);
    bottom: 0;
    height: 100%;
    pointer-events: none;
  }

  &::before {
    z-index: -1;
    width: calc(100% - var(--sub-level-gap) / 2);
    left: calc(var(--sub-level-gap) * 0.5);
  }

  &:after {
    left: calc(100% - var(--sub-level-gap));
    width: 200%;
    background-color: var(--overlay-background-second-level);
    z-index: -2;
  }

  &__item {
    &--active {
      font-weight: 700;

      .lower-level__link {
        color: var(--current-accent);
        background-color: var(--sub-menu-item-active-background);
        border-radius: 5px;
      }

      + .lower-level__item {
        .lower-level__link {
          border-top-color: transparent;
        }
      }
    }

    &:hover {
      border-radius: 5px;

      .lower-level__link {
        border-top-color: transparent;
        background-color: var(--sub-menu-item-hover-background);
        border-radius: 5px;
        color: var(--current-accent);
        font-weight: bold;
      }

      + .lower-level__item {
        .lower-level__link {
          border-top-color: transparent;
        }
      }
    }

    &:first-child {
      .lower-level__link {
        border-top: none;
      }
    }
  }

  &__link {
    padding: 10px;
    color: var(--default-text-color);
    padding: 8px 0;
    display: flex;
    align-items: center;
    text-decoration: none;
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 14px;
    padding-left: 12px;
    padding-right: 12px;
    border-top: 1px solid var(--sub-menu-item-hover-background);

    &:hover,
    &--active {
      color: var(--current-accent);
      border-radius: 5px;
      font-weight: bold;
    }
  }
}
</style>
